import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import TableFilters from "@/components/ui/table/data-table-filter";
import { statusValues } from "@/lib/constants/sale_history";
import { useGetMembersListQuery } from "@/services/memberApi";
import { useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";
import { useGetAllExtensionsQuery } from "@/services/extensionApi";
import { useGetCountersQuery } from "@/services/counterApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

interface SalesReportFiltersProps {
  dateRange?: {
    start_date?: string;
    end_date?: string;
  };
  appliedDateRange?: {
    start_date?: string;
    end_date?: string;
  };
  onDateRangeChange?: (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => void;
  onResetFilters?: () => void;
  onApplyFilters?: () => void;
  filterData: Record<string, any>;
  setFilterData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  openFilter: boolean;
  setOpenFilter: React.Dispatch<React.SetStateAction<boolean>>;
  initialValue: any;
  showAdvancedFilters?: boolean;
}

export default function SalesReportFilters({
  dateRange,
  appliedDateRange,
  onDateRangeChange,
  onResetFilters,
  onApplyFilters,
  filterData,
  setFilterData,
  openFilter,
  setOpenFilter,
  initialValue,
  showAdvancedFilters = true
}: SalesReportFiltersProps) {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  // Data queries
  const { data: membersData } = useGetMembersListQuery({
    id: Number(orgId),
    query: "",
  });

  const { data: counterList } = useGetCountersQuery(
    { query: `status=active` }
  );

  const countersOptions = useMemo(() => {
    return Array.isArray(counterList?.data) 
      ? counterList?.data.map((item) => ({
        value: item.id,
        label: item.name,
      })) 
      : [];
  }, [counterList]);

  function handleFilterChange(field: string, value: string | number | (string | number)[]) {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = useMemo(() => [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: statusValues.map((item) => ({
        id: item.value,
        name: item.label,
      })),
      function: (value: string) => handleFilterChange("status", value),
    },
     {
      type: "multiselect",
      name: "member_ids",
      label: "Members",
      options: membersData?.map((item) => ({
        value: item.value,
        label: item.label,
      })) ?? [],
      function: (value: (string | number)[]) => {
        handleFilterChange("member_ids", value);
      },
    },
    {
      type: "select",
      name: "type_ui",
      label: "Type",
      options: [
        { name: "Sales", id: "Sale" },
        { name: "Refund", id: "Refund" },
        // Unique UI value to distinguish selection while mapping to type=Sale
        { name: "Failed SRB Sales", id: "Sale_SRB" },
      ],
      function: (value: string, option?: { name?: string; id?: string }) => {
        setFilterData((prev) => {
          const isFailedSrb = option?.id === "Sale_SRB";
          const typeValue = value === "Refund" ? "Refund" : "Sale";
          const next = { ...prev } as Record<string, any>;
          next.type_ui = value; // keep UI selection distinct
          next.type = typeValue; // backend filter value
          if (isFailedSrb) {
            next.only_srb = true;
          } else {
            delete next.only_srb;
          }
          return next;
        });
      },
    },
    {
      type: "multiselect",
      name: "counter_ids",
      label: "Counter",
      options: countersOptions,
      function: (value: (string | number)[]) => {
        // Keep as array - parent will handle converting to query params
        handleFilterChange("counter_ids", value);
      },
    }
  ], [statusValues, membersData, countersOptions]);

  const handleApplyFiltersClick = () => {
    onApplyFilters?.();
  };

  const resetFilters = () => {
    setFilterData({});
    onResetFilters?.();
  };

  return (
    <>
      {/* Main Filter Controls */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-row gap-2 w-full items-center">
            {/* DatePicker - fixed width on desktop, full width on mobile */}
            <div className="flex-1 sm:flex-none">
              <DatePickerWithRange
                name={"Date Range"}
                value={{
                  start_date: dateRange?.start_date ? new Date(dateRange.start_date) : undefined,
                  end_date: dateRange?.end_date ? new Date(dateRange.end_date) : undefined,
                }}
                onValueChange={(dates) => {
                  onDateRangeChange?.(dates);
                }}
                label={"Select date range"}
                className="w-full sm:w-auto h-8"
              />
            </div>

            {/* Buttons - icon-only on mobile, full text on desktop */}
            <div className="flex flex-row gap-2 flex-shrink-0">
              <Button
                variant={"default"}
                className="text-sm bg-primary text-black flex items-center h-8 px-3 gap-2"
                onClick={handleApplyFiltersClick}
              >
                <i className="fa fa-filter text-sm"></i>
                <span className="hidden sm:inline">Apply</span>
              </Button>
              <Button
                variant={"outline"}
                className="text-sm text-black flex items-center h-8 px-2 gap-2"
                onClick={resetFilters}
              >
                <i className="fa-solid fa-arrows-rotate text-sm"></i>
                <span className="hidden sm:inline">Reset</span>
              </Button>

              {showAdvancedFilters && (
                <button
                  className="border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center"
                  onClick={() => setOpenFilter(true)}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <i className="fa fa-filter"></i>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Advanced filters</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters Modal */}
      {showAdvancedFilters && (
        <TableFilters
          isOpen={openFilter}
          setOpen={setOpenFilter}
          initialValue={initialValue}
          filterData={filterData}
          setFilter={setFilterData}
          setSearchCriteria={(value: any) => {
            // When user clicks "Apply filters" in the modal, call parent's apply function
            if (typeof value === 'function') {
              setFilterData(value);
            } else {
              setFilterData(value);
            }
            handleApplyFiltersClick();
          }}
          filterDisplay={filterDisplay}
          setInputValue={() => {}} // Not used in this context
          onReset={resetFilters}
        />
      )}
    </>
  );
}
